"use client";

import { useTranslations } from "next-intl";
import { Button } from "@artha/ui";
import type { ResumeReviewForm } from "../../personalization.types";
import { PERSONALIZATION_FIELD_CLASS_NAME } from "../../personalization.utils";

type ReviewStepProps = {
  form: ResumeReviewForm;
  isRequestingCode: boolean;
  onChange: (form: ResumeReviewForm) => void;
  onSubmit: () => void;
  submitLabel?: string;
  requestingLabel?: string;
};

export function ReviewStep({
  form,
  isRequestingCode,
  onChange,
  onSubmit,
  submitLabel,
  requestingLabel,
}: ReviewStepProps) {
  const t = useTranslations("profile.resumePersonalization");
  const fields = [
    ["name", t("review.name"), t("review.namePlaceholder"), true],
    ["email", t("review.email"), t("review.emailPlaceholder"), true],
    ["phone", t("review.phone"), t("review.phonePlaceholder"), false],
    ["titles", t("review.titles"), t("review.titlesPlaceholder"), true],
    ["skills", t("review.skills"), t("review.skillsPlaceholder"), true],
    ["keywords", t("review.keywords"), t("review.keywordsPlaceholder"), false],
    [
      "experienceYears",
      t("review.experienceYears"),
      t("review.experienceYearsPlaceholder"),
      false,
    ],
    [
      "educationLevel",
      t("review.educationLevel"),
      t("review.educationLevelPlaceholder"),
      false,
    ],
    [
      "educationField",
      t("review.educationField"),
      t("review.educationFieldPlaceholder"),
      false,
    ],
    ["city", t("review.city"), t("review.cityPlaceholder"), false],
    ["state", t("review.state"), t("review.statePlaceholder"), false],
    ["country", t("review.country"), t("review.countryPlaceholder"), false],
    ["workMode", t("review.workMode"), t("review.workModePlaceholder"), false],
  ] as const;

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      {fields.map(([field, label, placeholder, isRequired]) => (
        <label key={field} className="block space-y-1.5">
          <span className="text-sm font-semibold text-[#344054]">
            {label}
            {isRequired ? (
              <span className="text-[#9D1C1C] ml-0.5">*</span>
            ) : null}
          </span>
          <input
            type={
              field === "email"
                ? "email"
                : field === "experienceYears"
                  ? "number"
                  : "text"
            }
            min={field === "experienceYears" ? 0 : undefined}
            max={field === "experienceYears" ? 80 : undefined}
            step={field === "experienceYears" ? 1 : undefined}
            aria-label={label}
            required={isRequired}
            value={form[field]}
            placeholder={placeholder}
            className={PERSONALIZATION_FIELD_CLASS_NAME}
            onChange={(event) =>
              onChange({ ...form, [field]: event.target.value })
            }
          />
          {field === "titles" || field === "skills" || field === "keywords" ? (
            <span className="block text-xs text-[#667085]">
              {t("review.commaHint")}
            </span>
          ) : null}
        </label>
      ))}
      <Button
        type="submit"
        className="h-12 w-full rounded-[10px] bg-primary font-bold text-primary-foreground"
        disabled={isRequestingCode}
      >
        {isRequestingCode
          ? requestingLabel || t("actions.sendingCode")
          : submitLabel || t("review.submitCta")}
      </Button>
    </form>
  );
}
